import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import {
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1)
    }
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  },
  hint: {
    opacity: 0.7,
    marginTop: theme.spacing(1)
  },
  switchLine: {
    display: "flex",
    flexWrap: "wrap",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(2)
    }
  },
  tagChip: {
    color: "#FFF",
    fontWeight: 600,
    borderRadius: 3,
    fontSize: "0.8em"
  }
}));

const TagPhraseSchema = Yup.object().shape({
  phrase: Yup.string()
    .min(2, () => i18n.t("validation.tooShort"))
    .required(() => i18n.t("validation.required")),
  tagId: Yup.string().required(() => i18n.t("validation.required"))
});

const TagPhraseModal = ({ open, onClose, tagPhraseId, reload }) => {
  const classes = useStyles();

  const initialState = {
    phrase: "",
    tagId: ""
  };

  const [tagPhrase, setTagPhrase] = useState(initialState);
  const [active, setActive] = useState(true);
  const [exactMatch, setExactMatch] = useState(true);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        const { data } = await api.get("/tags/list");
        setTags(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        if (!tagPhraseId) {
          setTagPhrase(initialState);
          setActive(true);
          setExactMatch(true);
          return;
        }

        const { data } = await api.get(`/tag-phrases/${tagPhraseId}`);
        setActive(!!data.active);
        setExactMatch(!!data.exactMatch);
        setTagPhrase(prevState => ({
          ...prevState,
          phrase: data.phrase || "",
          // O Select trabalha com string; sem isso a opção não vem marcada na edição.
          tagId: data.tagId ? String(data.tagId) : ""
        }));
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagPhraseId, open]);

  const handleClose = () => {
    setTagPhrase(initialState);
    setActive(true);
    setExactMatch(true);
    onClose();
  };

  const handleSave = async values => {
    const payload = {
      ...values,
      tagId: Number(values.tagId),
      exactMatch,
      active
    };
    try {
      if (tagPhraseId) {
        await api.put(`/tag-phrases/${tagPhraseId}`, payload);
      } else {
        await api.post("/tag-phrases", payload);
      }
      toast.success(i18n.t("tagPhrases.modal.toasts.saved"));
      if (typeof reload === "function") {
        reload();
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth scroll="paper">
        <DialogTitle id="form-dialog-title">
          {tagPhraseId
            ? i18n.t("tagPhrases.modal.title.edit")
            : i18n.t("tagPhrases.modal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={tagPhrase}
          enableReinitialize={true}
          validationSchema={TagPhraseSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSave(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field
                    as={TextField}
                    label={i18n.t("tagPhrases.fields.phrase")}
                    name="phrase"
                    error={touched.phrase && Boolean(errors.phrase)}
                    helperText={touched.phrase && errors.phrase}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    autoFocus
                  />
                </div>
                <FormControl
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  error={touched.tagId && Boolean(errors.tagId)}
                >
                  <InputLabel id="tagphrase-tag-label">{i18n.t("tagPhrases.fields.tag")}</InputLabel>
                  <Field
                    as={Select}
                    label={i18n.t("tagPhrases.fields.tag")}
                    labelId="tagphrase-tag-label"
                    name="tagId"
                  >
                    {tags.map(tag => (
                      <MenuItem key={tag.id} value={String(tag.id)}>
                        <Chip
                          size="small"
                          className={classes.tagChip}
                          style={{ background: tag.color || "#eee" }}
                          label={(tag.name || "").toUpperCase()}
                        />
                      </MenuItem>
                    ))}
                  </Field>
                  {touched.tagId && errors.tagId && (
                    <FormHelperText>{errors.tagId}</FormHelperText>
                  )}
                </FormControl>
                <div className={classes.switchLine}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={exactMatch}
                        onChange={e => setExactMatch(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={i18n.t("tagPhrases.match.exact")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={active}
                        onChange={e => setActive(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={i18n.t("tagPhrases.status.active")}
                  />
                </div>
                <Typography variant="caption" component="p" className={classes.hint}>
                  {exactMatch
                    ? i18n.t("tagPhrases.modal.hints.exact")
                    : i18n.t("tagPhrases.modal.hints.contains")}
                </Typography>
                <Typography variant="caption" component="p" className={classes.hint}>
                  {i18n.t("tagPhrases.modal.hints.general")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {tagPhraseId ? i18n.t("common.save") : i18n.t("common.add")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TagPhraseModal;
